import { JSX, useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { ReferenceField, TextField, useRecordContext } from 'react-admin';
import { NokProcessingFailure } from '../../../../types/entities';
import { usePermissions } from 'react-admin';
import { loadFailureForResult, rescoreOne, RescoreResult } from './nokProcessingApi';
import { permissionManager } from '../../../permissions/manager';
import { cleanEntityString } from '../../../../providers/provider/defaults';
import { isStuckRetrying, STUCK_RETRY_MINUTES } from './failureRoutes';

/**
 * Why this result was never scored, and the button that sends it round again.
 *
 * Renders nothing when there is no open failure, which is almost always. It is deliberately not a
 * permanent empty panel saying "no problems": a result screen that always carries a failure box
 * trains people to stop reading it.
 *
 * The reason is shown in full here, unlike the list and the dashboard card where it is truncated.
 * This is the screen someone opens when they want to know what actually went wrong.
 */

type NokProcessingFailurePanelProps = {
    /** ms | cs. The result entities share no field that says which they are. */
    mini: 'ms' | 'cs',
}

/** How it failed, in words rather than the stored token. */
const stageLabel = (stage: string): string => {
    if (stage === 'timeout') {
        return 'The Operator never answered';
    }
    if (stage === 'dispatch') {
        return 'It never reached an Operator';
    }
    if (stage === 'refused') {
        return 'HQ-API refused the scores the Operator sent';
    }

    return 'The Operator gave up';
};

/** Who started the last rescore, as a sentence. The failures list has the one-word version. */
const lastRetryLabel = (source: string): string => {
    if (source === 'customer') {
        return 'Last rescore started by the customer';
    }
    if (source === 'automatic') {
        return 'Last rescore started automatically';
    }
    if (source === 'admin') {
        return 'Last rescore started from HQ';
    }

    return 'Last rescore started by ' + source;
};

export const NokProcessingFailurePanel: (props: NokProcessingFailurePanelProps) => JSX.Element | null = (
    { mini }: NokProcessingFailurePanelProps
): JSX.Element | null => {
    const record = useRecordContext<{ id?: number }>();
    const resultId: number | null = typeof record?.id === 'number' ? record.id : null;

    // HQ-21. Rescoring costs a full scoring pass per Nok, so it is its own ACL resource and
    // HQ-API refuses it without one. An ungated button is a button that can only fail.
    const { permissions } = usePermissions();
    const [failure, setFailure] = useState<NokProcessingFailure|null>(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [notice, setNotice] = useState<string|null>(null);

    const load = useCallback(async (isLive: () => boolean = (): boolean => true): Promise<void> => {
        if (resultId === null) {
            return;
        }
        try {
            const loaded: NokProcessingFailure|null = await loadFailureForResult(mini, resultId);
            if (isLive()) {
                setFailure(loaded);
            }
        } catch {
            // Swallowed on purpose, and the only place in this feature where that is right. This
            // panel is an extra on someone else's screen: a failed lookup must not put an error
            // over a result they opened to do something entirely different.
        }
    }, [mini, resultId]);

    useEffect((): (() => void) => {
        let live: boolean = true;
        void load((): boolean => live);

        return (): void => { live = false; };
    }, [load]);

    if (failure === null || resultId === null) {
        return null;
    }

    const retry = async (): Promise<void> => {
        setBusy(true);
        setNotice(null);
        const result: RescoreResult = await rescoreOne({ failureId: failure.id });
        setNotice(result.message);
        setBusy(false);
        if (result.status === 'success') {
            await load();
        }
    };

    // A queued rescore that has gone quiet can be sent again; one still inside its window cannot.
    const stuck: boolean = isStuckRetrying(failure);
    const waiting: boolean = failure.status === 'retrying' && !stuck;

    const lastRetrySource: string = failure.lastRetrySource ?? '';
    // The failure row carries the customer as an IRI, and customerDataProvider keys its records by
    // the bare id, as every other reference to `customers` does. Handed the IRI, the reference
    // loads the customer and then matches nothing, so it renders blank.
    const lastRetryCustomerId: string = failure.lastRetryCustomer
        ? cleanEntityString(failure.lastRetryCustomer, '/hq_api/customers/')
        : '';

    return (
        <Box sx={ { width: '100%', mb: 2 } }>
            <Alert
                severity={ waiting ? 'info' : 'error' }
                action={
                    permissionManager.isAllowed(permissions, 'HQ_Data::nok_rescore')
                    ? <Button
                        size="small"
                        color="inherit"
                        disabled={ busy || waiting }
                        onClick={ (): void => { void retry(); } }
                    >
                        { busy ? 'Queueing' : 'Rescore' }
                    </Button>
                    : undefined
                }
            >
                <AlertTitle>
                    { waiting
                        ? 'Queued to be scored again'
                        : stuck
                            ? 'Queued to be scored again, but nothing has happened for '
                                + STUCK_RETRY_MINUTES + ' minutes'
                            : 'This result was never scored' }
                </AlertTitle>
                <Typography variant="body2">
                    { /* A refused or timed-out row has no Operator attempts to count, and
                         "after 0 tries" reads as if nothing was tried. */ }
                    { stageLabel(failure.stage)
                        + (failure.attempts > 0
                            ? ' after ' + failure.attempts + (failure.attempts === 1 ? ' try.' : ' tries.')
                            : '.') }
                </Typography>
                { lastRetrySource !== '' && (
                    <Typography variant="body2" component="div" sx={ { mt: 1 } }>
                        { lastRetryLabel(lastRetrySource) }
                        { failure.lastRetryAt ? ' on ' + new Date(failure.lastRetryAt).toLocaleString() : '' }
                        { /* Who, for an admin who may open customers. Anyone else is told only that
                             it was a customer, rather than handed a reference that answers 403. */ }
                        { lastRetrySource === 'customer' && lastRetryCustomerId !== ''
                            && permissionManager.isAllowed(permissions, 'HQ_Customer::customers.view') && (
                            <>
                                { ': ' }
                                <ReferenceField
                                    record={ { id: failure.id, lastRetryCustomer: lastRetryCustomerId } }
                                    source="lastRetryCustomer"
                                    reference="customers"
                                    link="edit"
                                >
                                    <TextField source="email" />
                                </ReferenceField>
                            </>
                        ) }
                        { '.' }
                    </Typography>
                ) }
                { failure.reason !== null && failure.reason !== undefined && failure.reason !== '' && (
                    <Typography
                        variant="body2"
                        component="pre"
                        sx={ {
                            // The reason can be a stack trace, so it wraps and scrolls rather
                            // than pushing the form sideways.
                            mt: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 160,
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            fontSize: 12,
                        } }
                    >
                        { failure.reason }
                    </Typography>
                ) }
                { notice !== null && (
                    <Typography variant="body2" sx={ { mt: 1, fontWeight: 600 } }>{ notice }</Typography>
                ) }
            </Alert>
        </Box>
    );
}
